import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ReverseApi } from "~/functions/api/reverse";
import styles from "./index.module.scss";

interface SelectedAppForAnalysis {
  id?: string;
  appId?: string;
  name?: string;
  description?: string;
}

interface ConnectionData {
  connectionOverview?: unknown;
  parsedData?: {
    selectedApps?: unknown[];
    dependencies?: unknown[];
    relationships?: unknown[];
    appInfo?: Record<string, any>;
    statistics?: Record<string, unknown>;
    dependencyGraph?: Record<string, string[]>;
  } | null;
  dependencies?: unknown[];
  relationships?: unknown[];
  appInfo?: Record<string, unknown>;
  statistics?: Record<string, unknown>;
}

interface DependencyAnalysisStepProps {
  selectedApps: SelectedAppForAnalysis[];
  dfInstanceId: string;
  organizationId?: string;
  projectId?: string;
  reportId?: string;
  onComplete?: (data: ConnectionData) => void;
}

const TIMEOUT_MS = 3 * 60 * 1000; // 3分
const POLLING_INTERVAL_MS = 5000; // 5秒
const MAX_RETRIES = 3;

const DependencyAnalysisStep: React.FC<DependencyAnalysisStepProps> = ({
  selectedApps,
  dfInstanceId,
  organizationId,
  projectId,
  onComplete,
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const hasCompletedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const parseConnectionData = useCallback((connectionOverview: unknown) => {
    if (!connectionOverview) return null;

    let data: any = connectionOverview;
    if (typeof connectionOverview === "string") {
      try {
        data = JSON.parse(connectionOverview);
      } catch {
        return null;
      }
    }

    // app_info_dataに名前を追加（元のselectedAppsから取得）
    const appInfoWithNames = { ...data.app_info_data };
    if (data.selectedApps) {
      data.selectedApps.forEach((app: SelectedAppForAnalysis) => {
        const appId = app.appId || app.id;
        if (appId && appInfoWithNames[appId]) {
          appInfoWithNames[appId].name = app.name || appInfoWithNames[appId].name;
        }
      });
    }

    return {
      selectedApps: data.selectedApps || [],
      dependencies: data.app_dependencies || data.dependencies || [],
      relationships: data.relationships || [],
      appInfo: appInfoWithNames || {},
      statistics: data.statistics || {},
      dependencyGraph: data.dependency_graph || {},
    };
  }, []);

  const cleanup = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(async () => {
    if (!organizationId || !projectId || !dfInstanceId || hasCompletedRef.current) {
      return;
    }

    cleanup();

    setIsAnalyzing(true);
    setError(null);
    setProgress(0);

    abortControllerRef.current = new AbortController();
    const reverseApi = new ReverseApi();

    // タイムアウト設定
    timeoutTimerRef.current = setTimeout(() => {
      cleanup();
      setError("timeout");
      setIsAnalyzing(false);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.TIMEOUT);
    }, TIMEOUT_MS);

    const poll = async () => {
      if (hasCompletedRef.current || abortControllerRef.current?.signal.aborted) {
        return;
      }

      try {
        const statusData = await reverseApi.getInstanceStatus(
          organizationId,
          projectId,
          dfInstanceId,
        );

        if (!statusData) {
          return;
        }

        // 進捗更新
        const checkpoint = statusData.checkpoint ?? 0;
        setProgress(Math.min((checkpoint / 10) * 100, 90));

        // エラー状態のチェック
        if (statusData.isError) {
          hasCompletedRef.current = true;
          cleanup();
          setError("error");
          setIsAnalyzing(false);
          toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.DEPENDENCY_ANALYSIS_FAILED);
          return;
        }

        // connectionOverviewが存在すれば完了（developブランチの元のロジック）
        if (statusData.connectionOverview) {
          hasCompletedRef.current = true;
          cleanup();
          setProgress(100);
          setIsAnalyzing(false);

          const parsedData = parseConnectionData(statusData.connectionOverview);
          const connectionData = {
            connectionOverview: statusData.connectionOverview,
            parsedData: parsedData,
            dependencies: parsedData?.dependencies || [],
            relationships: parsedData?.relationships || [],
            appInfo: parsedData?.appInfo || {},
            statistics: parsedData?.statistics || {},
          };

          toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.DEPENDENCY_ANALYSIS_COMPLETE);
          onComplete?.(connectionData);
          return;
        }
      } catch (err: any) {
        console.error("Polling error:", err);
        // 404エラーの場合
        if (err?.code === "INFRASTRUCTURE_NOT_FOUND" || err?.statusCode === 404) {
          cleanup();
          setError("notfound");
          setIsAnalyzing(false);
          toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.RE_FAILED);
          return;
        }
      }
    };

    // 初回実行
    await poll();

    // ポーリング開始
    if (!hasCompletedRef.current && !abortControllerRef.current?.signal.aborted) {
      pollingTimerRef.current = setInterval(poll, POLLING_INTERVAL_MS);
    }
  }, [organizationId, projectId, dfInstanceId, cleanup, parseConnectionData, onComplete]);

  useEffect(() => {
    if (dfInstanceId && !hasCompletedRef.current) {
      startPolling();
    }

    return () => {
      cleanup();
    };
  }, [dfInstanceId, startPolling, cleanup]);

  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES) {
      return;
    }

    setRetryCount((prev) => prev + 1);
    hasCompletedRef.current = false;
    startPolling();
  }, [retryCount, startPolling]);

  const formatAppCount = (count: number) => {
    return `${count}個のアプリ`;
  };

  if (isAnalyzing) {
    return (
      <div className={styles["analysisContainer"]}>
        <div className={styles["loadingSection"]}>
          <div className={styles["spinner"]} />
          <h3 className={styles["loadingTitle"]}>依存関係を解析中...</h3>
          <p className={styles["loadingDescription"]}>
            選択された{formatAppCount(selectedApps.length)}の依存関係を解析しています。
          </p>
          <div className={styles["progressBar"]}>
            <div className={styles["progressFill"]} style={{ width: `${progress}%` }} />
          </div>
          <p className={styles["progressText"]}>{progress}%</p>
        </div>
      </div>
    );
  }

  if (error) {
    const canRetry = retryCount < MAX_RETRIES && error !== "notfound";
    const errorMessage =
      error === "timeout"
        ? "処理がタイムアウトしました（3分）。"
        : error === "notfound"
          ? "リバースエンジニアリングが失敗しました。最初からやり直してください。"
          : "依存関係の解析中に問題が発生しました。";

    return (
      <div className={styles["analysisContainer"]}>
        <div className={styles["errorSection"]}>
          <div className={styles["errorIcon"]}>⚠️</div>
          <h3 className={styles["errorTitle"]}>エラーが発生しました</h3>
          <p className={styles["errorMessage"]}>
            {errorMessage}
            {error !== "notfound" && (
              <>
                <br />
                <span className={styles["errorDetail"]}>処理を完了できませんでした。</span>
              </>
            )}
          </p>
          {canRetry && (
            <button className={styles["retryButton"]} onClick={handleRetry}>
              リトライ {retryCount + 1}/{MAX_RETRIES}
            </button>
          )}
          {retryCount >= MAX_RETRIES && (
            <p className={styles["errorDetail"]}>
              リトライ回数の上限に達しました。最初からやり直してください。
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles["analysisContainer"]}>
      <div className={styles["waitingSection"]}>
        <p>依存関係解析を開始するアプリを選択してください。</p>
      </div>
    </div>
  );
};

export default DependencyAnalysisStep;
